import React, { useCallback, useEffect, useMemo, useState } from "react";
import config from "../config.js";
import pinPrompt from "../utils/pinPrompt";
import { detailToString } from '../utils/apiError';

/**
 * "The spec just changed — what is still on the old one?"
 *
 * The question a buyer asks the moment a revision is cut, and the one JobQ
 * could not answer before: which open POs, sales orders, quotes and assembly
 * BOM rows are still pointing at a superseded revision, and which of them we
 * are actually allowed to move.
 *
 * Ineligible rows are shown, not hidden. A PO the vendor has already
 * acknowledged is the most important thing on this screen even though it is
 * the one row you cannot fix here — it is the one that needs a phone call.
 */
const GROUPS = [
  { key: "po_lines", idKey: "po_line_id", title: "Purchase orders", param: "po_line_ids" },
  { key: "so_lines", idKey: "so_line_id", title: "Sales orders", param: "so_line_ids" },
  { key: "quote_lines", idKey: "quote_part_id", title: "Quotes", param: "quote_part_ids" },
  {
    key: "assembly_bom_rows",
    idKey: "assembly_consumable_id",
    title: "Assembly BOMs",
    param: "assembly_consumable_ids",
  },
];

export default function ComponentExposureModal({ component, onClose, onApplied }) {
  const API_BASE = config.API_BASE_URL;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE}/components/${component.consumable_id}/open_order_exposure`
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(body.detail) || "Could not load open references.");
      setData(body);
      // Pre-select everything we are allowed to move — the common case is
      // "yes, all of it", and the ineligible rows stay visible but unticked.
      const next = {};
      GROUPS.forEach((g) => {
        (body[g.key] || []).forEach((r) => {
          if (r.eligible) next[`${g.key}:${r[g.idKey]}`] = true;
        });
      });
      setSelected(next);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, component]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    if (!data) return { total: 0, eligible: 0, chosen: 0 };
    let total = 0;
    let eligible = 0;
    GROUPS.forEach((g) => {
      const rows = data[g.key] || [];
      total += rows.length;
      eligible += rows.filter((r) => r.eligible).length;
    });
    return { total, eligible, chosen: Object.values(selected).filter(Boolean).length };
  }, [data, selected]);

  function toggle(groupKey, id) {
    setSelected((prev) => ({ ...prev, [`${groupKey}:${id}`]: !prev[`${groupKey}:${id}`] }));
  }

  async function handleApply() {
    const pin = await pinPrompt(`PIN to move these onto Rev ${data.current_revision_code}:`);
    if (!pin) return;
    setBusy(true);
    setError("");
    try {
      const body = { pin };
      GROUPS.forEach((g) => {
        const ids = (data[g.key] || [])
          .filter((r) => selected[`${g.key}:${r[g.idKey]}`])
          .map((r) => r[g.idKey]);
        if (ids.length) body[g.param] = ids;
      });
      const res = await fetch(
        `${API_BASE}/components/${component.consumable_id}/apply_current_revision`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(out.detail) || "Could not update those references.");
      await load();
      if (onApplied) onApplied(out);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  function rowLabel(groupKey, r) {
    if (groupKey === "po_lines")
      return `${r.po_number || `PO #${r.purchase_order_id}`} · ${r.quantity_ordered} ordered${
        r.quantity_received ? `, ${r.quantity_received} received` : ""
      } · ${r.po_status}`;
    if (groupKey === "so_lines")
      return `${r.order_number || `SO #${r.sales_order_id}`} · ${r.quantity_ordered} ordered · ${r.so_status}`;
    if (groupKey === "quote_lines")
      return `Quote #${r.quote_id} · ${r.quantity} · ${r.quote_status}`;
    return `${r.assembly_name || `Assembly #${r.assembly_id}`} · ${r.quantity_per_assembly} per assembly`;
  }

  return (
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,.5)", zIndex: 1055 }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Still on an older revision — {component.product_id}
              {data?.current_revision_code && (
                <span className="text-muted ms-2 fs-6">current is Rev {data.current_revision_code}</span>
              )}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}
            {loading && <div className="text-muted small">Loading…</div>}

            {!loading && counts.total === 0 && (
              <div className="alert alert-success py-2 mb-0">
                Nothing is on an older revision. Every open order, quote and BOM line already points
                at Rev {data?.current_revision_code}.
              </div>
            )}

            {!loading &&
              counts.total > 0 &&
              GROUPS.map((g) => {
                const rows = data[g.key] || [];
                if (!rows.length) return null;
                return (
                  <div className="mb-3" key={g.key}>
                    <div className="fw-semibold mb-1">
                      {g.title}{" "}
                      <span className="text-muted fw-normal small">
                        ({rows.filter((r) => r.eligible).length} of {rows.length} can be moved)
                      </span>
                    </div>
                    <ul className="list-group">
                      {rows.map((r) => {
                        const id = r[g.idKey];
                        const key = `${g.key}:${id}`;
                        return (
                          <li
                            key={key}
                            className={`list-group-item d-flex align-items-start gap-2 ${
                              r.eligible ? "" : "list-group-item-light"
                            }`}
                          >
                            <input
                              className="form-check-input mt-1"
                              type="checkbox"
                              disabled={!r.eligible || busy}
                              checked={!!selected[key]}
                              onChange={() => toggle(g.key, id)}
                            />
                            <div className="flex-grow-1">
                              <div>{rowLabel(g.key, r)}</div>
                              <div className="small">
                                <span className="badge bg-secondary me-2">
                                  Rev {r.stamped_revision_code || "—"}
                                </span>
                                {!r.eligible && <span className="text-danger">{r.reason}</span>}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
          </div>

          <div className="modal-footer">
            <span className="me-auto small text-muted">
              {counts.chosen} selected · {counts.total - counts.eligible} cannot be moved
            </span>
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Close
            </button>
            <button
              className="btn btn-primary"
              disabled={busy || counts.chosen === 0}
              onClick={handleApply}
            >
              {busy ? "Updating…" : `Move ${counts.chosen} onto Rev ${data?.current_revision_code || ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
